import React, { useState, useEffect } from "react";
import Cart from "./Cart";
import Shipping, { generateDateOptions } from "./Shipping";
import Contact from "./Contact";
import Checkout from "./Checkout";
import Confirmation from "./Confirmation";
import Success from "./Success";

function CartModal({
  cart,
  setCart,
  onUpdateQuantity,
  onRemoveItem,
  onCloseModal,
  theme,
  store,
  storeSchedule,
  refreshInventory,
  total,
  setTotal,
  shippingMethod,
  setShippingMethod,
  deliveryOption,
  setDeliveryOption,
  deliveryFee,
  setDeliveryFee,
  tax,
  setTax,
  deliveryDate,
  setDeliveryDate,
  deliveryTime,
  setDeliveryTime,
  pickupDate,
  setPickupDate,
  pickupTime,
  setPickupTime,
  deliveryAddress,
  setDeliveryAddress,
  deliveryCity,
  setDeliveryCity,
  deliveryState,
  setDeliveryState,
  deliveryZip,
  setDeliveryZip,
  contactFirstName,
  setContactFirstName,
  contactLastName,
  setContactLastName,
  contactBusinessName,
  setContactBusinessName,
  contactEmail,
  setContactEmail,
  contactPhone,
  setContactPhone,
  billingFirstName,
  setBillingFirstName,
  billingLastName,
  setBillingLastName,
  billingEmail,
  setBillingEmail,
  billingAddress,
  setBillingAddress,
  billingPhone,
  setBillingPhone,
  billingCity,
  setBillingCity,
  billingState,
  setBillingState,
  billingZip,
  setBillingZip,
  deliveryOptions,
  setDeliveryOptions,
  totalDriveTime,
  setTotalDriveTime,
  optionsReceived,
  setOptionsReceived,
  formattedDeliveryAddress,
  setFormattedDeliveryAddress,
  routeDetails,
  setRouteDetails,
  discount,
  setDiscount,
  discountCode,
  setDiscountCode,
  discountRate,
  setDiscountRate,
  discountCodeId,
  setDiscountCodeId,
  confirmationCode,
  setConfirmationCode,
  orderId,
  setOrderId,
  availableDateOptions,
  setAvailableDateOptions
}) {
  const taxRate = 0.08;
  const [step, setStep] = useState("cart");

  function sumProducts(items) {
    return items.reduce((sum, item) => {
      const price = Number(item.retail_price?.replace(/,/g, ""));
      return sum + (Number.isFinite(price) ? price * item.quantity : 0);
    }, 0);
  }

  const productTotal = sumProducts(cart);
  const taxableTotal = sumProducts(cart.filter(
    (item) => item.taxable === true || item.taxable.toLowerCase() === "true"
  ));
  const taxablePercentage = (taxableTotal / productTotal);

  useEffect(() => {
    const newTaxTotal = (productTotal - discount) * taxablePercentage * taxRate; // Use taxRate from props
    setTax(newTaxTotal);
  }, [productTotal, discount, taxablePercentage, taxRate, setTax]);

  useEffect(() => {
    const newDiscount = productTotal * discountRate;
    setDiscount(newDiscount);
  }, [productTotal, discountRate, setDiscount]);

  useEffect(() => {
    setTotal(productTotal + deliveryFee + tax - discount);
  }, [productTotal, deliveryFee, tax, discount, setTotal]);

  useEffect(() => {  
    if (storeSchedule && storeSchedule.length > 0) {
      const now = new Date();
      const maxLeadTimeMinutes = Math.max(...cart.map(item => (Number(item.lead_time_hours) || 0) * 60));
      
      // This is the time when the items should be available and the vendor can start preparing the order
      const readyToPrepTime = new Date(
        now.getTime() + 
        maxLeadTimeMinutes * 60000
      );
      const minutesToPrepareOrder = Number(store["prep_time"]);
      const minutesToLoadOrder = Number(store["load_time"]);

      const minutesToLoadAndDeliver = shippingMethod === "deliver" ? minutesToLoadOrder + totalDriveTime : 0;
      const generatedDateOptions = generateDateOptions(
        storeSchedule, 
        readyToPrepTime, 
        minutesToPrepareOrder,
        minutesToLoadAndDeliver, 
        shippingMethod
      );
      
      setAvailableDateOptions(generatedDateOptions);
    }
  }, [cart, shippingMethod, formattedDeliveryAddress, storeSchedule, store, totalDriveTime, setAvailableDateOptions]);

  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },
    modalContainer: {
      backgroundColor: theme.backgroundColor,
      color: "white",
      borderRadius: "10px",
      width: cart.length === 0 ? "400px" : "90vw",
      maxWidth: cart.length === 0 ? "400px" : "600px",
      height: cart.length === 0 ? "auto" : "80dvh", //dynamic viewport height (fixes iphone overlapping toolbar issue)
      minHeight: cart.length === 0 ? "200px" : "auto",
      padding: "20px",
      position: "relative",
      boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.3)",
      overflowY: "auto", // Enable scrolling for overflow content
      display: "flex",
      flexDirection: "column", // Ensure content stacks correctly
      WebkitOverflowScrolling: "touch", // Improve scroll on iOS devices
    },
    closeButton: {
      position: "absolute",
      top: "10px",
      right: "10px",
      backgroundColor: "transparent",
      color: theme.primaryFontColor,
      border: "none",
      padding: "8px 12px",
      borderRadius: "5px",
      cursor: "pointer",
      fontSize: 32,
      zIndex: 1001,
    },
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modalContainer}>
        <button
          style={styles.closeButton}
          onClick={() => {
            if (step === "success") {
              setConfirmationCode("");
            }
            onCloseModal(); // Call the onCloseModal function
          }}
        >
          &times;
        </button>
        <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {step === "cart" && (
            <Cart
              store={store}
              cart={cart}
              onUpdateQuantity={onUpdateQuantity}
              onRemoveItem={onRemoveItem}
              onProceed={() => setStep("shipping")}
              theme={theme}
              total={total}
              tax={tax}
              productTotal={productTotal}
              discount={discount}
              discountCode={discountCode}
              setDiscount={setDiscount}
              setDiscountCode={setDiscountCode}
              discountRate={discountRate}
              setDiscountRate={setDiscountRate}
              setDiscountCodeId={setDiscountCodeId}
            />
          )}
          {step === "shipping" && (
            <Shipping
              store={store}
              discount={discount}
              shippingMethod={shippingMethod}
              setShippingMethod={setShippingMethod}
              deliveryOption={deliveryOption}
              setDeliveryOption={setDeliveryOption}
              deliveryFee={deliveryFee}
              setDeliveryFee={setDeliveryFee}
              deliveryDate={deliveryDate}
              setDeliveryDate={setDeliveryDate}
              deliveryTime={deliveryTime}
              setDeliveryTime={setDeliveryTime}
              deliveryAddress={deliveryAddress}
              setDeliveryAddress={setDeliveryAddress}
              deliveryCity={deliveryCity}
              setDeliveryCity={setDeliveryCity}
              deliveryState={deliveryState}
              setDeliveryState={setDeliveryState}
              deliveryZip={deliveryZip}
              setDeliveryZip={setDeliveryZip}
              pickupDate={pickupDate}
              setPickupDate={setPickupDate}
              pickupTime={pickupTime}
              setPickupTime={setPickupTime}
              deliveryOptions={deliveryOptions}
              setDeliveryOptions={setDeliveryOptions}
              onProceed={() => setStep("confirmation")}
              onBack={() => setStep("cart")}
              theme={theme}
              total={total}
              tax={tax}
              productTotal={productTotal}
              setTotalDriveTime={setTotalDriveTime}
              storeSchedule={storeSchedule}
              optionsReceived={optionsReceived}
              setOptionsReceived={setOptionsReceived}
              setFormattedDeliveryAddress={setFormattedDeliveryAddress}
              cart={cart}
              routeDetails={routeDetails}
              setRouteDetails={setRouteDetails}
              availableDateOptions={availableDateOptions}
            />
          )}

          {step === "confirmation" && (
            <Confirmation
              cart={cart}
              onUpdateQuantity={onUpdateQuantity}
              onRemoveItem={onRemoveItem}
              onProceed={() => setStep("contact")}
              onBack={() => setStep("shipping")}
              theme={theme}
              total={total} // Pass total down
              deliveryFee={deliveryFee}
              tax={tax}
              setTax={setTax}
              shippingMethod={shippingMethod}
              deliveryOption={deliveryOption}
              deliveryAddress={deliveryAddress}
              deliveryCity={deliveryCity}
              deliveryState={deliveryState} // Pass related variable
              deliveryDate={deliveryDate} // Pass related variable
              deliveryTime={deliveryTime} // Pass related variable
              totalDriveTime={totalDriveTime}
              pickupDate={pickupDate}
              pickupTime={pickupTime}
              store={store}
              taxRate={taxRate}
              productTotal={productTotal}
              formattedDeliveryAddress={formattedDeliveryAddress}
              discount={discount}
              routeDetails={routeDetails}
            />
          )}
          {step === "contact" && (
            <Contact
              contactFirstName={contactFirstName} // Pass related variable
              setContactFirstName={setContactFirstName} // Pass setter
              contactLastName={contactLastName} // Pass related variable
              setContactLastName={setContactLastName} // Pass setter
              contactBusinessName={contactBusinessName} // Pass related variable
              setContactBusinessName={setContactBusinessName} // Pass setter
              contactEmail={contactEmail} // Pass related variable
              setContactEmail={setContactEmail} // Pass setter
              contactPhone={contactPhone} // Pass related variable
              setContactPhone={setContactPhone} // Pass setter
              onProceed={() => setStep("checkout")}
              theme={theme}
              onBack={() => setStep("confirmation")}
              total={total}
            />
          )}
          {step === "checkout" && (
            <Checkout
              billingFirstName={billingFirstName}
              setBillingFirstName={setBillingFirstName}
              billingLastName={billingLastName}
              setBillingLastName={setBillingLastName}
              billingEmail={billingEmail}
              setBillingEmail={setBillingEmail}
              billingAddress={billingAddress}
              setBillingAddress={setBillingAddress}
              billingPhone={billingPhone}
              setBillingPhone={setBillingPhone}
              billingCity={billingCity}
              setBillingCity={setBillingCity}
              billingState={billingState}
              setBillingState={setBillingState}
              billingZip={billingZip}
              setBillingZip={setBillingZip}
              cart={cart}
              theme={theme}
              closeModal={onCloseModal}
              onBack={() => setStep("contact")}
              onSuccess={() => setStep("success")}
              total={total}
              setCart={setCart}
              deliveryFee={deliveryFee}
              tax={tax}
              discount={discount}
              discountCodeId={discountCodeId}
              discountCode={discountCode}
              setTax={setTax}
              productTotal={productTotal}
              // Pass shipping and contact details
              contactFirstName={contactFirstName}
              contactLastName={contactLastName}
              contactBusinessName={contactBusinessName}
              contactEmail={contactEmail}
              contactPhone={contactPhone}
              shippingMethod={shippingMethod}
              deliveryOption={deliveryOption}
              deliveryDate={deliveryDate}
              deliveryTime={deliveryTime}
              deliveryAddress={deliveryAddress}
              deliveryCity={deliveryCity}
              deliveryState={deliveryState}
              deliveryZip={deliveryZip}
              pickupDate={pickupDate}
              pickupTime={pickupTime}
              totalDriveTime={totalDriveTime}
              setShippingMethod={setShippingMethod}
              setDeliveryOption={setDeliveryOption}
              setDeliveryDate={setDeliveryDate}
              setDeliveryTime={setDeliveryTime}
              setDeliveryAddress={setDeliveryAddress}
              setDeliveryCity={setDeliveryCity}
              setDeliveryState={setDeliveryState}
              setDeliveryZip={setDeliveryZip}
              setPickupDate={setPickupDate}
              setPickupTime={setPickupTime}
              setTotalDriveTime={setTotalDriveTime}
              setContactFirstName={setContactFirstName}
              setContactLastName={setContactLastName}
              setContactBusinessName={setContactBusinessName}
              setContactEmail={setContactEmail}
              setContactPhone={setContactPhone}
              store={store}
              confirmationCode={confirmationCode}
              setConfirmationCode={setConfirmationCode}
              orderId={orderId}
              setOrderId={setOrderId}
            />
          )}

          {step === "success" && (
            <Success
              confirmationCode={confirmationCode}
              onClose={onCloseModal}
              setConfirmationCode={setConfirmationCode}
              theme={theme}
              refreshInventory={refreshInventory}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default CartModal;
